// src/encounter/lib/medication-record-to-seed.ts
//
// `medActions` (Samer's Loom, 3 Nov: "change, refill, stop, and then it goes to the encounter part"): a chart medication's FULL record
// (GET GetMedicationDetails) as the A&P's existing medication seed: the picked medication for today's dose dialog, and its schedule
// as the dialog's pre-fill. Continue, Refill and Change all open that dialog in the Plan; the clinician saves it as today's plan
// order (Refill is a new order for the same drug and dose until a PostRefill hook exists, R1). Nothing is invented: a field the
// record does not hold stays absent, and the start date is not carried (the new order starts from the visit).
import { toMedicationEditPrefill, type FullMedicationRecord } from './to-medication-request';
import type { MedicationDose } from './to-medication-order';

/** Left-rail target §3: each rail medication action reaches its own handler. Stop is a reason-confirmed discontinue (#906's
 *  `ApMedStopHost`, today's PostStopMedication); the other three open the A&P dose dialog (the drug-interaction gate X2 lives there). */
export type MedicationPlanIntent = 'continue' | 'refill' | 'change' | 'stop';

/** What a medication intent does on the encounter page. Pure, so each intent's route is pinned by a test. */
export type MedicationPlanRoute =
  /** The A&P dose dialog, pre-filled with the recorded drug and schedule. The intent is NOT carried past this route: Refill and
   *  Edit dose both open today's dose dialog (Refill with the recorded dose kept, Edit dose for the clinician to change it), because
   *  that dialog is where the drug-interaction gate X2 runs (left-rail target §3: keep today's gate). A Refill that orders with no
   *  dialog needs the X2 recheck on a pending line first (a gap). */
  | { kind: 'dose-dialog'; intent: 'continue' | 'refill' | 'change'; prefill: boolean }
  /** The stop confirm (`ApMedStopHost`). */
  | { kind: 'stop' };

export function medicationPlanRoute(intent: MedicationPlanIntent): MedicationPlanRoute {
  switch (intent) {
    case 'stop':
      return { kind: 'stop' };
    case 'refill':
      // R1: the same medicine and dose as a new order (no PostRefill hook exists yet).
      return { kind: 'dose-dialog', intent, prefill: true };
    case 'change':
      // The dose dialog, the recorded dose shown so the clinician changes it.
      return { kind: 'dose-dialog', intent, prefill: true };
    case 'continue':
    default:
      return { kind: 'dose-dialog', intent: 'continue', prefill: true };
  }
}

export interface MedicationSeed {
  item: { id: string; code?: string; name: string; system?: string; route?: { code: string; display: string }[]; unitOfMeasurement?: string };
  dictatedDose: MedicationDose;
}

const num = (v: string | undefined): number | undefined => {
  if (v == null || v.trim() === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

/** The seed for the A&P dose dialog, or null when the record names no medication (nothing to seed). */
export function medicationRecordToSeed(full: FullMedicationRecord): MedicationSeed | null {
  const p = toMedicationEditPrefill(full);
  const name = p.medication.display.trim();
  const code = p.medication.code.trim();
  if (!name && !code) return null;
  const dose: MedicationDose = {};
  if (p.route) {
    dose.route = p.route.code;
    dose.routeDisplay = p.route.display;
  }
  if (p.unitOfMeasurement) dose.doseUnit = p.unitOfMeasurement;
  if (p.frequency) dose.frequency = p.frequency;
  const quantity = num(p.doseCount);
  if (quantity != null) dose.quantity = quantity;
  if (p.timesADay) dose.timesADay = p.timesADay;
  if (p.daysInterval) dose.daysInterval = p.daysInterval;
  if (p.specificDays?.length) dose.specificDays = p.specificDays;
  const duration = num(p.duration);
  if (duration != null) dose.duration = duration;
  if (p.durationType) dose.durationUnit = p.durationType;
  return {
    item: {
      id: code || name,
      name: name || code,
      ...(code ? { code } : {}),
      ...(p.medication.system ? { system: p.medication.system } : {}),
      ...(p.route ? { route: [{ code: p.route.code, display: p.route.display }] } : {}),
      ...(p.unitOfMeasurement ? { unitOfMeasurement: p.unitOfMeasurement } : {}),
    },
    dictatedDose: dose,
  };
}

/** The A&P Active Medications row (an Rx order, `toApActiveMedications` id = order id) as the patient's medication record it
 *  belongs to: the record whose `orderId` is the order's; else, by code, ONLY a record linked to no order (#906 review: a record of
 *  another order with the same drug must never be the one stopped). Null when nothing matches (nothing to continue or stop here). */
export function patientMedicationForOrder(
  medications: readonly { identifier?: string | null; orderId?: string | null; isDeleted?: boolean | null; code?: { code?: string | null; display?: string | null } | null }[] | null | undefined,
  orders: readonly { id?: string | null; orderId?: string | null; medicationRequest?: { code?: { code?: string | null } | null } | null }[] | null | undefined,
  rowId: string,
): { identifier: string; display: string } | null {
  const order = (orders ?? []).find((o) => (o.id ?? o.orderId) === rowId);
  if (!order) return null;
  const code = order.medicationRequest?.code?.code?.trim();
  const live = (medications ?? []).filter((m) => !m.isDeleted && m.identifier);
  const m =
    (order.orderId ? live.find((x) => x.orderId === order.orderId) : undefined) ??
    (code ? live.find((x) => !x.orderId && x.code?.code?.trim() === code) : undefined);
  return m?.identifier ? { identifier: m.identifier, display: m.code?.display ?? '' } : null;
}
